class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));
    this.summaryToggle.setAttribute('role', 'button');
  }
  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }
  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }
  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }
  // Restyled header search: blur the page + header chrome (NOT the announcement bar)
  // while the panel is open. A CSS backdrop-filter can't reach the page from inside the
  // sticky header's stacking context, and `filter` on an ancestor blurs its whole subtree,
  // so we blur specific elements directly. Three parts:
  //   1) Header chrome only: walk from .header__search up to (and including) the header
  //      section, blurring the SIBLINGS of each ancestor (logo, nav, other icons). We STOP
  //      at .section-header so its siblings — notably the announcement bar — are left sharp.
  //   2) The search toggle (magnifier <summary>) itself: it lives inside .header__search
  //      (kept sharp overall) but should blur with the rest of the chrome when open. It's a
  //      sibling of .search-modal, so blurring it doesn't touch the panel/overlay.
  //   3) Page body + footer, added explicitly (the walk stops before them).
  // No blurred element contains another, so blur never compounds.
  psBgBlurTargets() {
    const targets = [];
    const skip = { SCRIPT: 1, STYLE: 1, LINK: 1, NOSCRIPT: 1, TEMPLATE: 1 };
    const stop =
      this.closest('.section-header') || this.closest('.header-wrapper') || document.querySelector('.section-header');
    // 1) Header chrome (siblings along the path), stopping inside the header section.
    let node = this;
    while (node && node !== stop && node.parentElement) {
      const parent = node.parentElement;
      for (const sib of parent.children) {
        if (sib !== node && sib.nodeType === 1 && !skip[sib.tagName]) targets.push(sib);
      }
      if (parent === stop) break;
      node = parent;
    }
    // 2) The header magnifier (search toggle).
    const summary = this.querySelector('summary');
    if (summary) targets.push(summary);
    // 3) Main content + footer (announcement bar deliberately excluded).
    document
      .querySelectorAll('#MainContent, main, .shopify-section-group-footer-group, .footer')
      .forEach((el) => targets.push(el));
    return targets;
  }
  psSetBgBlur(on) {
    if (on) {
      this._psBlurred = this.psBgBlurTargets();
      this._psBlurred.forEach((el) => el.classList.add('ps-bg-blur'));
    } else {
      (this._psBlurred || this.psBgBlurTargets()).forEach((el) => el.classList.remove('ps-bg-blur'));
      this._psBlurred = null;
    }
  }
  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    // Restyled header search: drop the panel below the sticky header/announcement bar
    // instead of pinning it to the very top of the viewport, and blur the page behind.
    const isHeaderSearch = this.classList.contains('header__search');
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (isHeaderSearch) {
      const header = document.querySelector('.section-header');
      const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().top)) : 0;
      this.style.setProperty('--ps-top', `${top}px`);
      this.psSetBgBlur(true);
      // Mobile: the panel opens collapsed to the search field, then expands to full
      // height once results render. Measure the resting (field-only) height so the
      // max-height animation starts from the real height with no jump. Transform on
      // the slide-in shifts top+bottom equally, so (bottom - top) is measured cleanly.
      const panel = this.detailsContainer.querySelector('.search-modal__content');
      const field = panel && panel.querySelector('.field');
      if (panel && field) {
        requestAnimationFrame(() => {
          const h = field.getBoundingClientRect().bottom - panel.getBoundingClientRect().top;
          if (h > 0) this.style.setProperty('--ps-collapsed-h', `${Math.ceil(h + 16)}px`);
        });
      }
    }
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');
    // On mobile the header search must NOT auto-focus the input, because that raises the
    // on-screen keyboard before the user asks for it. Focus the panel container instead;
    // tapping the search field then focuses it and shows the keyboard on demand. Desktop
    // keeps input focus so you can type immediately.
    const focusContainer = this.detailsContainer.querySelector('[tabindex="-1"]');
    const focusInput = this.detailsContainer.querySelector('input:not([type="hidden"])');
    trapFocus(focusContainer, isHeaderSearch && isMobile ? focusContainer : focusInput);
  }
  close(focusToggle = true) {
    // Un-blur the page as the panel starts to close so the two animate together.
    if (this.classList.contains('header__search')) {
      this.psSetBgBlur(false);
    }
    // Restyled header search: play a slide-up animation before removing [open] so the
    // panel animates out instead of vanishing. Scoped to .header__search; every other
    // details-modal closes instantly as before.
    if (
      this.classList.contains('header__search') &&
      this.detailsContainer.hasAttribute('open') &&
      !this.classList.contains('is-closing') &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      const content = this.detailsContainer.querySelector('.search-modal__content');
      if (content) {
        this.classList.add('is-closing');
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          content.removeEventListener('animationend', finish);
          this.classList.remove('is-closing');
          this.finishClose(focusToggle);
        };
        content.addEventListener('animationend', finish);
        // Safety net in case animationend never fires (e.g. animation stripped by CSS).
        setTimeout(finish, 450);
        return;
      }
    }
    this.finishClose(focusToggle);
  }
  finishClose(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}
customElements.define('details-modal', DetailsModal);
